import { getDeviceProperty } from '@/api';
import { deviceControl, DeviceProperty } from '@/api/device-thingmodel';
import { AppstoreOutlined, EditOutlined, HistoryOutlined, LineChartOutlined } from '@ant-design/icons';

import { Card, Col, Divider, Empty, Form, Input, message, Pagination, Radio, Row, Select, Switch, Typography } from 'antd';
import dayjs from 'dayjs';
import { isNull, isObject } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import MatrixChart from './MatrixChart';
import { Tab1Modal, Tab1ModalRef } from './Tab1Modal';
import { WriteValueModal } from './WriteValueModal';

const { Text, Title } = Typography;

/** 滤芯倒计时属性标识符，初始值12个月（365天），每天递减 */
const FILTER_COUNTDOWN_CODES = ['UF', 'PP'];
const FILTER_COUNTDOWN_TOTAL_DAYS = 365;

/** 计算滤芯剩余天数：从时间戳计算已过天数，用初始天数减去 */
const getFilterRemainingDays = (timestampValue: number | string): number | null => {
  const ts = Number(timestampValue);
  if (!ts || isNaN(ts)) return null;
  // 时间戳单位可能是秒或毫秒，统一处理
  const tsMs = ts > 1e12 ? ts : ts * 1000;
  const startDate = new Date(tsMs);
  const now = new Date();
  const elapsedDays = Math.floor((now.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
  return Math.max(0, FILTER_COUNTDOWN_TOTAL_DAYS - elapsedDays);
};

export interface DetailTab1Props {
  product_id?: string;
}

export const DetailTab1: React.FC<DetailTab1Props> = ({ product_id }) => {
  const params = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const timer = useRef<ReturnType<typeof setInterval>>();
  const tab1ModalRef = useRef<Tab1ModalRef>(null);

  const [lists, setLists] = useState<DeviceProperty[]>([]);
  const [checked, setChecked] = useState<boolean>(true);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<{ [key: string]: boolean }>({});
  const [viewMode, setViewMode] = useState<'chart' | 'list'>('list');

  const [pageParams, setPageParams] = useState<{ page: number; pageSize: number; info?: string; type?: 'info' | 'tag' }>({ page: 1, pageSize: 16 });
  const pageParamsRef = useRef<{ page: number; pageSize: number; info?: string; type?: 'info' | 'tag' }>({ page: 1, pageSize: 16 });

  const [writeModalVisible, setWriteModalVisible] = useState(false);
  const [currentItem, setCurrentItem] = useState<DeviceProperty>();

  const load = useCallback(
    (changeParams?: { page?: number; pageSize?: number; info?: string; type?: 'info' | 'tag' }) => {
      const base = pageParamsRef.current;
      console.log({ changeParams });
      const { type, info, ...rest } = { ...base, ...changeParams };
      getDeviceProperty({ id: params.id!, ...rest, ...(type === 'tag' ? { tag: info } : { info }) }).then((resp) => {
        console.log(resp, { changeParams });
        if (resp.success) {
          setLists(resp.result.list);
          setTotal(resp.result.total);

          const code = searchParams.get('code');

          if (code) {
            const item = resp.result.list.find((item) => item.code === code);

            item && setTimeout(() => tab1ModalRef.current?.open(item), 0);
          }
        }
      });
    },
    [params, searchParams],
  );

  useEffect(() => {
    if (checked) {
      timer.current = setInterval(() => {
        load();
      }, 3000);
    } else {
      clearInterval(timer.current);
    }

    return () => {
      clearInterval(timer.current);
    };
  }, [checked]);

  useEffect(() => {
    // 初始化 info（来源于 url: ?code=xxx）并保存到 ref/state
    const initialInfo = searchParams.get('code') || undefined;
    if (initialInfo) {
      pageParamsRef.current = { ...pageParamsRef.current, info: initialInfo };
      setPageParams((prev) => ({ ...prev, info: initialInfo }));
      load({ info: initialInfo });
    } else {
      load();
    }
  }, []);

  // if (!lists.length) {
  //   return <Empty />;
  // }

  const handleChange = async (item: DeviceProperty) => {
    setLoading((prev) => ({ ...prev, [item.code]: true }));
    try {
      const res = await deviceControl({ deviceId: params.id!, data: { [item.code]: !item.value } });
      if (res.success) {
        message.success('操作成功');
        load();
      } else {
        message.error(res.errorMsg);
      }
      setLoading((prev) => ({ ...prev, [item.code]: false }));
    } catch (error) {
      setLoading((prev) => ({ ...prev, [item.code]: false }));
      console.log(error);
    }
  };

  const handleWriteValue = async (value: string | number | boolean) => {
    if (!currentItem) return;

    // 类型转换已在 WriteValueModal 中处理，这里直接使用
    try {
      const res = await deviceControl({ deviceId: params.id!, data: { [currentItem.code]: value } } as any);
      if (res.success) {
        message.success('写入成功');
        load();
        setWriteModalVisible(false);
      } else {
        message.error(res.errorMsg);
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      <Tab1Modal ref={tab1ModalRef} product_id={product_id}></Tab1Modal>
      <WriteValueModal open={writeModalVisible} onCancel={() => setWriteModalVisible(false)} onOk={handleWriteValue} item={currentItem} />
      <div className="mb10" style={{ display: 'flex', justifyContent: viewMode === 'list' ? 'space-between' : 'flex-end', alignItems: 'center', gap: '16px' }}>
        {viewMode === 'list' && (
          <Form layout="inline" initialValues={{ code: searchParams.get('code'), type: 'info' }}>
            <Form.Item name="type">
              <Select
                allowClear={false}
                options={[
                  { label: '名称', value: 'info' },
                  { label: '标签', value: 'tag' },
                ]}
                style={{ width: 100 }}
              />
            </Form.Item>
            <Form.Item dependencies={['type']} style={{ width: 200 }}>
              {({ getFieldValue }) => (
                <Input.Search
                  width={200}
                  placeholder="请输入查询条件"
                  allowClear
                  onSearch={(info) => {
                    const type = getFieldValue('type');
                    console.log({ type, info });
                    const searchInfo = info?.trim() || undefined;
                    pageParamsRef.current = { ...pageParamsRef.current, page: 1, info: searchInfo, type };
                    setPageParams({ ...pageParamsRef.current, type });
                    load({ page: 1, info: searchInfo, type });
                  }}
                ></Input.Search>
              )}
            </Form.Item>
          </Form>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Radio.Group value={viewMode} onChange={(e) => setViewMode(e.target.value)}>
            <Radio.Button value="list" style={{ lineHeight: '34px' }}>
              <AppstoreOutlined style={{ fontSize: '16px' }} />
            </Radio.Button>
            <Radio.Button value="chart" style={{ lineHeight: '34px' }}>
              <LineChartOutlined style={{ fontSize: '16px' }} />
            </Radio.Button>
          </Radio.Group>
          <Form layout="inline">
            <Form.Item label="实时刷新">
              <Switch checked={checked} onChange={setChecked} />
            </Form.Item>
          </Form>
        </div>
      </div>
      {viewMode === 'list' ? (
        <>
          {lists?.length === 0 && <Empty />}
          <Row gutter={[16, 16]}>
            {lists.map((item, index) => {
              return (
                <Col span={6} key={index}>
                  <Card className="property-card" hoverable size="small">
                    <div className="justify-space-between">
                      <div>
                        <Text style={{ color: '#000000', fontSize: 14 }}>{item.name}</Text>
                      </div>
                      <div>
                        <Text
                          className="on-hover"
                          style={{ fontSize: 12, color: '#000000' }}
                          onClick={() => {
                            tab1ModalRef.current?.open(item);
                          }}
                        >
                          <HistoryOutlined className="mr5" />
                          历史数据
                        </Text>
                      </div>
                    </div>
                    <div className="mt10 mb20 align-center justify-space-between">
                      {FILTER_COUNTDOWN_CODES.includes(item.code) ? (
                        <div style={{ textAlign: 'center' }}>
                          {(() => {
                            const remainingDays = getFilterRemainingDays(item.value as number | string);
                            if (remainingDays === null) {
                              return <Title level={5} style={{ fontSize: 18, margin: 0, color: '#999' }}>未设置</Title>;
                            }
                            const isExpired = remainingDays <= 30;
                            return (
                              <>
                                <Title level={5} style={{ fontSize: 24, margin: 0, color: isExpired ? '#ff4d4f' : '#1890ff' }}>
                                  {remainingDays}
                                </Title>
                                <Text type="secondary" style={{ fontSize: 12 }}>天（剩余）</Text>
                              </>
                            );
                          })()}
                        </div>
                      ) : (
                        <Title level={5} ellipsis={{ tooltip: isNull(item.value) ? '-' : isObject(item.value) ? JSON.stringify(item.value) : String(item.value) }} style={{ fontSize: 18, margin: 0 }}>
                          {isNull(item.value) ? '-' : isObject(item.value) ? JSON.stringify(item.value) : String(item.value)}
                          {item.unit && <> ({item.unit})</>}
                        </Title>
                      )}
                      <div className="align-center">
                        {item?.data_type !== 'bool' && item?.access_mode?.toLowerCase() === 'rw' && (
                          <EditOutlined
                            style={{ fontSize: '14px', cursor: 'pointer' }}
                            onClick={() => {
                              setCurrentItem(item);
                              setWriteModalVisible(true);
                            }}
                          />
                        )}
                        {item?.data_type === 'bool' && item?.access_mode?.toLowerCase() === 'rw' && (
                          <Switch key={item.code} checked={!!lists.find((v) => v.code === item.code)?.value} onChange={() => handleChange(item)} loading={loading[item.code]} className="ml10" />
                        )}
                      </div>
                    </div>
                    <div className="mt20">
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {dayjs(item.time).format('YYYY-MM-DD HH:mm:ss')}
                        <Divider type="vertical" />
                        {item.data_type}
                        <Divider type="vertical" />
                        {item.access_mode?.toLowerCase() === 'ro' || item.access_mode?.toLowerCase() === 'r' ? '只读' : '读写'}
                      </Text>
                    </div>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </>
      ) : (
        <>
          {lists?.length === 0 && <Empty />}
          <MatrixChart height={600} deviceId={params.id!} />
        </>
      )}
      {viewMode === 'list' && (
        <div className="mt20" style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Pagination
            current={pageParams.page}
            pageSize={pageParams.pageSize}
            onChange={(page, pageSize) => {
              pageParamsRef.current = { ...pageParamsRef.current, page, pageSize };
              setPageParams({ ...pageParamsRef.current });
              load({ page, pageSize });
            }}
            showSizeChanger
            showTotal={(total) => `共 ${total} 条`}
            showQuickJumper
            showPrevNextJumpers
            total={total}
          ></Pagination>
        </div>
      )}
    </>
  );
};

export default DetailTab1;
